/**
 * The ten colours an identity is drawn in — one voice in a transcript, one person in
 * the gallery.
 *
 * The point of the colour is recognition: the same identity has to be the same colour
 * everywhere it appears. So a caller derives it from something about that identity that
 * does not change — a speaker's position in the recording, a person's id — rather than
 * assigning one as it renders. What the ramp itself has to guarantee is only that ten
 * of them are tellable apart.
 *
 * Ten, not four
 * -------------
 * Four was the count while a speaker row meant a person and five while a gallery was
 * small. Clusters are split deliberately now, eight to ten rows on a six-person meeting
 * are ordinary, and at four the fifth row came back round to the first speaker's colour
 * — the one thing the colour exists to prevent.
 *
 * Where the colours come from
 * ---------------------------
 * Not from more tokens. The palette only ever defines four brand hues — a chosen palette
 * rotates all four together (see scripts/build-palettes.mjs), and the `-soft` and
 * `-muted` steps are lightness, not hue, so they read as the same identity dimmed. The
 * ramp is generated instead: ten hues 36° apart, anchored on `--vl-bridge` and carrying
 * its lightness and chroma unchanged.
 *
 * Anchoring rather than inventing is what keeps this legible. These colours are drawn
 * as text, as a dot, and as a disc with initials on it, and `--vl-bridge` is already
 * tuned to work at those sizes in every palette and both modes — clamped into a
 * lightness band by the palette build, and given its own value under `.light`. Taking
 * its `l` and `c` and turning only `h` means all ten inherit that tuning, with no second
 * set of values to keep in step. Holding lightness still also means the initials on an
 * avatar sit at the same contrast whichever colour a person drew.
 */

/** Ten slots around the wheel from `--vl-bridge`, 36° apart. */
const SLOTS = 10;

/**
 * Which slot each position takes. Not 0, 1, 2… — neighbouring slots are only 36° apart,
 * and consecutive positions are exactly the ones read next to each other in a segment
 * list, so the order hops the wheel instead of walking it: every consecutive pair is at
 * least 72° apart except the third and fourth, which sit where the handoff's own warm
 * and violet already sat.
 *
 * The first four land within a few degrees of the four brand hues in the default
 * palette, in the brand order, so a meeting with four voices or fewer — the common one —
 * wears the four brand hues. A caller keyed by a hash rather than a
 * position gets nothing from the ordering and is not harmed by it either: the table is a
 * permutation, so it spreads exactly as well as the hash did.
 */
const ORDER = [0, 8, 3, 2, 6, 9, 4, 7, 1, 5];

/**
 * One slot's colour. Odd slots take 72% of the anchor's chroma: the only pairs sitting
 * at the 36° floor are slots one apart, and one of any such pair is odd, so the two
 * closest hues are also the vivid one and the pale one. Lightness is never touched —
 * that is what carries the contrast.
 */
function slotColour(slot: number): string {
  if (slot === 0) return "var(--vl-bridge)";

  const chroma = slot % 2 === 0 ? "c" : "calc(c * 0.72)";

  return `oklch(from var(--vl-bridge) l ${chroma} calc(h + ${slot * 36}))`;
}

/** The colour for a position in the ramp. Any integer; it wraps, in both directions. */
export function identityColour(position: number): string {
  return slotColour(ORDER[((position % SLOTS) + SLOTS) % SLOTS]!);
}
